import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import './App.css';
import { FolderList } from './components/FolderList';
import { ScenarioEditor } from './components/ScenarioEditor';
import { ScenarioImport } from './components/ScenarioImport';
import { TestCaseExport } from './components/TestCaseExport';
import { RegressionSuite } from './components/RegressionSuite';
import { VersionHistory } from './components/VersionHistory';
import { Folder, apiClient } from './services/api';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProtectedRoute, useRBAC } from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { OAuthCallbackPage } from './pages/OAuthCallbackPage';
import { AdminConsolePage } from './pages/AdminConsolePage';
import { AllScenariosPage } from './pages/AllScenariosPage';
import { TestCasesPage } from './pages/TestCasesPage';
import TestRunListPage from './pages/TestRunListPage';
import TestRunDetailPage from './pages/TestRunDetailPage';
import CreateTestRunPage from './pages/CreateTestRunPage';
import { ProjectDashboard } from './components/ProjectDashboard';
import { HelpCenterPage } from './pages/HelpCenterPage';
import { GlobalSearch } from './components/GlobalSearch';
import { AnalyticsDashboardPage } from './pages/AnalyticsDashboardPage';
import SharedFolderPage from './pages/SharedFolderPage';
import { NotificationPanel } from './components/NotificationPanel';

// Placeholder for role management page
function RoleManagementPage() {
  return (
    <div className="role-management-page">
      <h2>Role & Permission Management</h2>
      <p>Manage roles and their permissions</p>
      {/* TODO: Implement role management UI */}
    </div>
  );
}

// User Profile Component
function UserProfile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setShowDropdown(false);
  };

  if (!user) {
    return null;
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="user-profile-container">
      <div className="user-profile" onClick={() => setShowDropdown(!showDropdown)}>
        <div className="user-avatar">{getInitials(user.name)}</div>
        <div className="user-info">
          <div className="user-name">{user.name}</div>
          <div className="user-role">{user.role}</div>
        </div>
      </div>
      {showDropdown && (
        <div className="user-dropdown">
          <button className="dropdown-item" onClick={handleLogout}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M10 3.5a.5.5 0 0 0-.5-.5h-8a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h8a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 1 1 0v2A1.5 1.5 0 0 1 9.5 14h-8A1.5 1.5 0 0 1 0 12.5v-9A1.5 1.5 0 0 1 1.5 2h8A1.5 1.5 0 0 1 11 3.5v2a.5.5 0 0 1-1 0v-2z"/>
              <path d="M15.854 8.354a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708.708L14.293 7.5H5.5a.5.5 0 0 0 0 1h8.793l-2.147 2.146a.5.5 0 0 0 .708.708l3-3z"/>
            </svg>
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

// Home Page - Projects List (Flat view, no hierarchy)
function HomePage() {
  const navigate = useNavigate();
  const [refreshKey] = useState(0);

  const handleFolderSelect = (folder: Folder) => {
    navigate(`/folders/${folder.id}`);
  };

  return (
    <div className="home-page">
      <FolderList 
        key={refreshKey}
        onFolderSelect={handleFolderSelect} 
        showTree={false}
        hideCreateButton={false}
      />
    </div>
  );
}

// Folder Detail Page - Shows scenarios in a folder
function FolderDetailPage() {
  const { folderId } = useParams<{ folderId: string }>();
  const [folder, setFolder] = useState<Folder | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const loadFolder = async () => {
    try {
      setLoading(true);
      const folderData = await apiClient.getFolder(folderId!);
      setFolder(folderData);
    } catch (error) {
      console.error('Error loading folder:', error);
      alert('Failed to load folder');
    } finally {
      setLoading(false);
    }
  };

  const loadBreadcrumbs = async () => {
    try {
      const breadcrumbData = await apiClient.getBreadcrumb(folderId!);
      setBreadcrumbs(breadcrumbData);
    } catch (error) {
      console.error('Error loading breadcrumbs:', error);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (folderId) {
      loadFolder();
      loadBreadcrumbs();
    }
  }, [folderId]);

  if (loading) {
    return <div className="loading">Loading folder...</div>;
  }

  if (!folder) {
    return <div className="error">Folder not found</div>;
  }

  return (
    <div className="folder-detail-page">
      {breadcrumbs.length > 0 && (
        <div className="breadcrumb-container">
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <ol className="breadcrumb-list">
              <li className="breadcrumb-item">
                <Link to="/" className="breadcrumb-link">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8.707 1.5a1 1 0 00-1.414 0L.646 8.146a.5.5 0 00.708.708L2 8.207V13.5A1.5 1.5 0 003.5 15h9a1.5 1.5 0 001.5-1.5V8.207l.646.647a.5.5 0 00.708-.708L13 5.793V2.5a.5.5 0 00-.5-.5h-1a.5.5 0 00-.5.5v1.293L8.707 1.5zM13 7.207V13.5a.5.5 0 01-.5.5h-9a.5.5 0 01-.5-.5V7.207l5-5 5 5z"/>
                  </svg>
                  Home
                </Link>
              </li>
              {breadcrumbs.map((item: any, index: number) => (
                <li key={item.id} className="breadcrumb-item">
                  <span className="breadcrumb-separator">/</span>
                  {index === breadcrumbs.length - 1 ? (
                    <span className="breadcrumb-current">{item.name}</span>
                  ) : (
                    <Link to={`/folders/${item.id}`} className="breadcrumb-link">
                      {item.name}
                    </Link>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        </div>
      )}

      {/* Project Dashboard - Replaces old folder detail content */}
      <ProjectDashboard folderId={folderId!} folderName={folder.name} folderDescription={folder.description} />
    </div>
  );
}

// Scenario Editor Page
function ScenarioEditorPage() {
  const { folderId, scenarioId } = useParams<{ folderId: string; scenarioId?: string }>();
  const navigate = useNavigate();

  const handleSave = () => {
    navigate(`/folders/${folderId}`);
  };

  const handleCancel = () => {
    navigate(`/folders/${folderId}`);
  };

  return (
    <div className="scenario-editor-page">
      <h2>{scenarioId ? 'Edit Scenario' : 'Create New Scenario'}</h2>
      <ScenarioEditor
        folderId={folderId!}
        scenarioId={scenarioId}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </div>
  );
}

// Scenario Import Page
function ScenarioImportPage() {
  const { folderId } = useParams<{ folderId: string }>();
  const navigate = useNavigate();

  const handleImportComplete = () => {
    navigate(`/folders/${folderId}`);
  };

  const handleCancel = () => {
    navigate(`/folders/${folderId}`);
  };

  return (
    <div className="scenario-import-page">
      <h2>Import Test Scenarios</h2>
      <ScenarioImport
        folderId={folderId!}
        onImportComplete={handleImportComplete}
        onCancel={handleCancel}
      />
    </div>
  );
}

// Test Case Export Page
function TestCaseExportPage() {
  const { folderId } = useParams<{ folderId: string }>();
  const navigate = useNavigate();

  const handleExportComplete = () => {
    navigate(`/test-cases`);
  };

  return (
    <div className="test-case-export-page">
      <h2>Export Test Cases</h2>
      <TestCaseExport folderId={folderId!} onExportComplete={handleExportComplete} />
    </div>
  );
}



// Regression Suite Page
function RegressionSuitePage() {
  const { folderId } = useParams<{ folderId: string }>();
  const [folder, setFolder] = useState<Folder | null>(null);
  const [loading, setLoading] = useState(true);

  const loadFolder = async () => {
    try {
      setLoading(true);
      const folderData = await apiClient.getFolder(folderId!);
      setFolder(folderData);
    } catch (error) {
      console.error('Error loading folder:', error);
      alert('Failed to load folder');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (folderId) {
      loadFolder();
    }
  }, [folderId]);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!folder) {
    return <div className="error">Folder not found</div>;
  }

  return (
    <div className="regression-suite-page">
      <h2>Regression Suite Execution - {folder.name}</h2>
      <RegressionSuite folder={folder} />
    </div>
  );
}

// Test Execution Page - Shows test run with all test cases
// Version History Page
function VersionHistoryPage() {
  const { scenarioId } = useParams<{ scenarioId: string }>();

  return (
    <div className="version-history-page">
      <h2>Scenario Version History</h2>
      <VersionHistory scenarioId={scenarioId!} />
    </div>
  );
}

// Main App Component with Authentication
function AppContent() {
  const { user, logout } = useAuth();
  const { isViewer } = useRBAC();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const location = useLocation();

  // Check if current route is a public route (login/register/oauth-callback)
  const isPublicRoute = location.pathname === '/login' || location.pathname === '/register' || location.pathname === '/oauth-callback';

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="App">
      {/* Only show header and sidebar for authenticated routes */}
      {!isPublicRoute && (
        <>
          <header className="App-header">
            <div className="header-content">
              <GlobalSearch />
              <div className="header-actions">
                <NotificationPanel />
                <button className="header-icon-btn" title="Settings">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
                  </svg>
                </button>
                <UserProfile />
              </div>
            </div>
          </header>

          <div className="app-body">
            <aside className={`sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
              {/* Logo */}
              <div className="sidebar-logo">
                <div className="sidebar-logo-icon">🛡️</div>
                <div className="sidebar-logo-text">
                  <h1>AssureX</h1>
                  <p>Powered for Scalable Test Management</p>
                </div>
              </div>

              <nav className="sidebar-nav">
                {!isViewer && (
                  <Link to="/" className={`nav-item${location.pathname === '/' ? ' active' : ''}`} title="Dashboard">
                    <svg className="nav-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                    </svg>
                    <span className="nav-text">Dashboard</span>
                  </Link>
                )}
                {!isViewer && (
                  <Link to="/projects" className={`nav-item${location.pathname === '/projects' ? ' active' : ''}`} title="Projects">
                    <svg className="nav-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>
                    </svg>
                    <span className="nav-text">Projects</span>
                  </Link>
                )}
                <Link to="/test-scenarios" className={`nav-item${location.pathname.startsWith('/test-scenarios') || location.pathname.startsWith('/scenarios') ? ' active' : ''}`} title="Test Scenarios">
                  <svg className="nav-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
                  </svg>
                  <span className="nav-text">Test Scenarios</span>
                </Link>
                <Link to="/test-cases" className={`nav-item${location.pathname.startsWith('/test-cases') ? ' active' : ''}`} title="Test Cases">
                  <svg className="nav-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                  </svg>
                  <span className="nav-text">Test Cases</span>
                </Link>
                {!isViewer && (
                  <Link to="/test-runs" className={`nav-item${location.pathname.startsWith('/test-runs') ? ' active' : ''}`} title="Test Runs">
                    <svg className="nav-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/>
                    </svg>
                    <span className="nav-text">Test Runs</span>
                  </Link>
                )}
                {user?.role === 'Admin' && (
                  <>
                    <div className="nav-divider"></div>
                    <Link to="/admin" className={`nav-item${location.pathname.startsWith('/admin') ? ' active' : ''}`} title="Admin Console">
                      <svg className="nav-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      </svg>
                      <span className="nav-text">Admin Console</span>
                    </Link>
                  </>
                )}
              </nav>

              {/* Bottom links */}
              <div className="sidebar-bottom">
                {isViewer && (
                  <div className="sidebar-viewer-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                    </svg>
                    <span>View Only</span>
                  </div>
                )}
                <Link to="/help" className={`nav-item${location.pathname === '/help' ? ' active' : ''}`} title="Help Center">
                  <svg className="nav-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <span className="nav-text">Help Center</span>
                </Link>
                <button
                  className="nav-item"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', width: '100%', textAlign: 'left' }}
                  onClick={() => { logout(); }}
                  title="Logout"
                >
                  <svg className="nav-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  <span className="nav-text">Logout</span>
                </button>
              </div>

              <div className="sidebar-toggle-bottom">
                <button onClick={toggleSidebar} title={sidebarCollapsed ? "Expand" : "Collapse"}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    {sidebarCollapsed ? (
                      <path d="M9 18l6-6-6-6"/>
                    ) : (
                      <path d="M15 18l-6-6 6-6"/>
                    )}
                  </svg>
                </button>
              </div>
            </aside>

            <main className="App-main">
              <div className="container">
                <Routes>
                  {/* Public routes */}
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/oauth-callback" element={<OAuthCallbackPage />} />

                  {/* Protected routes */}
                  <Route path="/" element={<ProtectedRoute requireRole="QA_Engineer"><AnalyticsDashboardPage /></ProtectedRoute>} />
                  <Route path="/projects" element={<ProtectedRoute requireRole="QA_Engineer"><HomePage /></ProtectedRoute>} />
                  <Route path="/test-suites/:folderId" element={<ProtectedRoute><FolderDetailPage /></ProtectedRoute>} />
                  <Route path="/test-suites/:folderId/scenarios/new" element={<ProtectedRoute><ScenarioEditorPage /></ProtectedRoute>} />
                  <Route path="/test-suites/:folderId/scenarios/:scenarioId/edit" element={<ProtectedRoute><ScenarioEditorPage /></ProtectedRoute>} />
                  <Route path="/test-suites/:folderId/scenarios/:scenarioId/versions" element={<ProtectedRoute><VersionHistoryPage /></ProtectedRoute>} />
                  <Route path="/test-suites/:folderId/import" element={<ProtectedRoute><ScenarioImportPage /></ProtectedRoute>} />
                  <Route path="/test-suites/:folderId/export" element={<ProtectedRoute><TestCaseExportPage /></ProtectedRoute>} />
                  <Route path="/test-suites/:folderId/regression" element={<ProtectedRoute><RegressionSuitePage /></ProtectedRoute>} />
                  <Route path="/test-scenarios" element={<ProtectedRoute><AllScenariosPage /></ProtectedRoute>} />
                  <Route path="/test-scenarios/*" element={<ProtectedRoute><AllScenariosPage /></ProtectedRoute>} />
                  <Route path="/scenarios" element={<ProtectedRoute><AllScenariosPage /></ProtectedRoute>} />
                  <Route path="/test-cases" element={<ProtectedRoute><TestCasesPage /></ProtectedRoute>} />
                  <Route path="/test-cases/*" element={<ProtectedRoute><TestCasesPage /></ProtectedRoute>} />
                  <Route path="/test-runs" element={<ProtectedRoute requireRole="QA_Engineer"><TestRunListPage /></ProtectedRoute>} />
                  <Route path="/test-runs/create" element={<ProtectedRoute requireRole="QA_Engineer"><CreateTestRunPage /></ProtectedRoute>} />
                  <Route path="/test-runs/:testRunId" element={<ProtectedRoute requireRole="QA_Engineer"><TestRunDetailPage /></ProtectedRoute>} />
                  <Route path="/folders/:folderId" element={<ProtectedRoute><FolderDetailPage /></ProtectedRoute>} />
                  <Route path="/folders/:folderId/scenarios/new" element={<ProtectedRoute><ScenarioEditorPage /></ProtectedRoute>} />
                  <Route path="/folders/:folderId/scenarios/:scenarioId/edit" element={<ProtectedRoute><ScenarioEditorPage /></ProtectedRoute>} />
                  <Route path="/folders/:folderId/import" element={<ProtectedRoute><ScenarioImportPage /></ProtectedRoute>} />
                  <Route path="/folders/:folderId/export" element={<ProtectedRoute><TestCaseExportPage /></ProtectedRoute>} />
                  
                  {/* Admin only routes */}
                  <Route path="/admin" element={<ProtectedRoute requireAdmin={true}><AdminConsolePage /></ProtectedRoute>} />
                  <Route path="/admin/roles" element={<ProtectedRoute requireAdmin={true}><RoleManagementPage /></ProtectedRoute>} />
                  <Route path="/help" element={<ProtectedRoute><HelpCenterPage /></ProtectedRoute>} />
                  <Route path="/shared/:token" element={<ProtectedRoute><SharedFolderPage /></ProtectedRoute>} />
                </Routes>
              </div>
            </main>
          </div>

          <footer className="App-footer">
            <p>AssureX v1.0.0 • Powered for Scalable Test Management</p>
          </footer>
        </>
      )}

      {/* For public routes, render without header/sidebar/footer */}
      {isPublicRoute && (
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/oauth-callback" element={<OAuthCallbackPage />} />
        </Routes>
      )}
    </div>
  );
}

// Main App Component with Router and Auth Provider
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
